import { useMutation } from "@tanstack/react-query";
import { useState, type ReactNode, type SyntheticEvent } from "react";

import type { BillingOverview, BillingReconcileResponse } from "../../shared/billing-schemas";
import type { OpsCustomerSummary } from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { linkStripeCustomer, reconcileStripe } from "./billing-api";
import { TextField } from "./billing-fields";
import {
  fieldState,
  REASON_REQUIREMENT,
  useFormErrors,
  type FieldGuide,
} from "./billing-validation";
import { BillingEmailForm } from "./BillingEmailForm";
import { usePrimaryContact } from "./use-primary-contact";

/**
 * The Stripe card of the Billing tab (ADR-0073): which Stripe customer this is, where Stripe
 * emails invoices, and the link and reconcile actions. A billing email starts as the primary
 * contact's (ADR-0088), so linking a customer needs no retyping.
 */

const LINK_GUIDE: FieldGuide = {
  rename: (field) => (field === "billingEmail" ? "email" : field),
  requirements: {
    email: "Optional. Where Stripe emails invoices and receipts, like ap@customer.example.",
    reason: REASON_REQUIREMENT,
  },
};

function StripeLinkForm({
  customerSlug,
  suggestedEmail,
  onLinked,
}: {
  customerSlug: string;
  suggestedEmail: string | null;
  onLinked: () => Promise<void>;
}) {
  // Untouched, the email is the primary contact's; it arrives after the form mounts.
  const [typed, setEmail] = useState<string | null>(null);
  const email = typed ?? suggestedEmail ?? "";
  const [reason, setReason] = useState("");
  const errors = useFormErrors(LINK_GUIDE);
  const link = useMutation({
    mutationFn: () =>
      linkStripeCustomer(customerSlug, {
        billingEmail: email.trim() === "" ? null : email.trim(),
        reason: reason.trim(),
      }),
    onSuccess: onLinked,
    onError: errors.fail,
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    link.mutate();
  }
  return (
    <form
      className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-start"
      onSubmit={submit}
      aria-label="Link to Stripe"
      noValidate
    >
      <TextField
        {...fieldState(errors, LINK_GUIDE.requirements, "email", "stripe-email")}
        label="Billing email (optional)"
        type="email"
        value={email}
        onChange={(value) => {
          errors.clear("email");
          setEmail(value);
        }}
      />
      <TextField
        {...fieldState(errors, LINK_GUIDE.requirements, "reason", "stripe-reason")}
        label="Reason"
        value={reason}
        required
        maxLength={500}
        onChange={(value) => {
          errors.clear("reason");
          setReason(value);
        }}
      />
      <div className="flex sm:pt-7">
        <Button type="submit" disabled={link.isPending}>
          Create Stripe customer
        </Button>
      </div>
      {errors.summary ? <FieldError className="sm:col-span-3">{errors.summary}</FieldError> : null}
    </form>
  );
}

function stripeStanding(billing: BillingOverview): ReactNode {
  if (!billing.stripeConfigured && billing.stripeMode === "test")
    return "Stripe is off: production has a Stripe test-mode key, and production only uses live Stripe. Terms and hand-recorded billing still work.";
  if (!billing.stripeConfigured)
    return "Stripe is not connected in this environment. Terms and hand-recorded billing still work.";
  if (billing.customer === null) return "Not linked to a Stripe customer yet.";
  return (
    <>
      Linked to Stripe customer{" "}
      <span className="font-mono text-foreground">{billing.customer.stripeCustomerId}</span>
      {billing.customer.billingEmail ? ` · invoices go to ${billing.customer.billingEmail}` : ""}
      {billing.stripeMode === "test" ? " · test mode" : ""}
    </>
  );
}

function StripeLine({ billing }: { billing: BillingOverview }) {
  const noEmail = billing.customer !== null && billing.customer.billingEmail === null;
  return (
    <p className="min-w-0 max-w-3xl text-muted-foreground">
      <span id="billing-stripe-title" className="mr-2 font-semibold text-foreground">
        Stripe
      </span>
      {stripeStanding(billing)}
      {noEmail ? " · no billing email yet, so Stripe cannot email invoices" : ""}
    </p>
  );
}

function ReconcileResult({
  data,
  error,
}: {
  data: BillingReconcileResponse | undefined;
  error: Error | null;
}) {
  if (error) return <FieldError className="mt-2">{error.message}</FieldError>;
  if (!data) return null;
  return (
    <p className="mt-2 text-sm text-muted-foreground" role="status">
      Checked {data.invoices} invoices, {data.subscriptions} subscriptions, {data.refunds} refunds;{" "}
      {data.changed} changed.
    </p>
  );
}

/** The form under the line: linking an unlinked customer, or the billing email being edited. */
function LineForm({
  customerSlug,
  billing,
  suggestedEmail,
  editingEmail,
  onEmailDone,
  refresh,
}: {
  customerSlug: string;
  billing: BillingOverview;
  suggestedEmail: string | null;
  editingEmail: boolean;
  onEmailDone: (saved: boolean) => Promise<void>;
  refresh: () => Promise<void>;
}) {
  if (billing.customer === null)
    return (
      <StripeLinkForm
        customerSlug={customerSlug}
        suggestedEmail={suggestedEmail}
        onLinked={refresh}
      />
    );
  if (!editingEmail) return null;
  return (
    <BillingEmailForm
      customerSlug={customerSlug}
      current={billing.customer.billingEmail}
      suggested={suggestedEmail}
      onCancel={() => {
        void onEmailDone(false);
      }}
      onDone={() => onEmailDone(true)}
    />
  );
}

type CardProps = {
  customer: OpsCustomerSummary;
  billing: BillingOverview;
  canCollect: boolean;
  canReadContacts: boolean;
  refresh: () => Promise<void>;
};

/** The small actions on a linked customer's line: its billing email, and a refresh from Stripe. */
function LinkedActions({
  billing,
  onEditEmail,
  reconcile,
}: {
  billing: BillingOverview;
  onEditEmail: () => void;
  reconcile: { pending: boolean; run: () => void };
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="ghost" onClick={onEditEmail}>
        {billing.customer?.billingEmail ? "Change billing email" : "Set billing email"}
      </Button>
      <Button type="button" variant="ghost" disabled={reconcile.pending} onClick={reconcile.run}>
        Refresh from Stripe
      </Button>
    </div>
  );
}

/**
 * One line saying which Stripe customer this is and where invoices go, with its two small actions.
 * Linking an unlinked customer, or changing the email, opens a short form under the line.
 */
export function BillingStripeCard({
  customer,
  billing,
  canCollect,
  canReadContacts,
  refresh,
}: CardProps) {
  const contacts = usePrimaryContact(customer.id, canCollect && canReadContacts);
  const suggestedEmail = contacts.primary?.email ?? null;
  const [editingEmail, setEditingEmail] = useState(false);
  const reconcile = useMutation({
    mutationFn: () => reconcileStripe(customer.slug),
    onSuccess: refresh,
  });
  const actionable = canCollect && billing.stripeConfigured;
  const linked = billing.customer !== null;
  return (
    <section
      aria-labelledby="billing-stripe-title"
      className="rounded-xl bg-card px-6 py-4 shadow-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StripeLine billing={billing} />
        {actionable && linked ? (
          <LinkedActions
            billing={billing}
            onEditEmail={() => {
              setEditingEmail(true);
            }}
            reconcile={{
              pending: reconcile.isPending,
              run: () => {
                reconcile.mutate();
              },
            }}
          />
        ) : null}
      </div>
      {actionable ? (
        <LineForm
          customerSlug={customer.slug}
          billing={billing}
          suggestedEmail={suggestedEmail}
          editingEmail={editingEmail}
          onEmailDone={async (saved) => {
            setEditingEmail(false);
            if (saved) await refresh();
          }}
          refresh={refresh}
        />
      ) : null}
      <ReconcileResult data={reconcile.data} error={reconcile.error} />
    </section>
  );
}
